import {
  BaseRequiredDialogProps,
  Button,
  FilterIcon,
  LoadingSkeleton,
  Select,
} from '@bloomfieldrobotics/canopy';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import {
  IconButton,
  IconButtonProps,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import config from '../../../../config';
import { Block } from '../../../../services/entities/impl/blfApi/schemas/block';
import { Stage } from '../../../../services/entities/impl/blfApi/schemas/stage';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import {
  MapControl,
  MapControlContent,
  MapControlFooter,
  MapControlHeader,
  MapControlSection,
} from '../../../commonUI/maps/controls';
import { PointIdCopyControl } from '../../../commonUI/maps/controls/PointIdCopyControl';
import { PointImage } from '../../../commonUI/maps/controls/PointImage/PointImage';
import { DiagnosticFeature } from '../../../commonUI/viewMode/DiagnosticFeature';
import { StageSelect } from '../../../reports/blueberry/components/StageSelect';
import { BlockPointData } from '../hooks/useGetBlockPoints';
import { PointStageDataDisplay } from './StageDataDisplay/PointStageDataDisplay/PointStageDataDisplay';
import {
  BlockPointsMapControlActionTypes,
  BlockPointsMapControlFilters,
} from './types';

export type BlockPointDetailsControlProps = {
  block: Block;
  point: BlockPointData | undefined;
  stages: Stage[];
  onNextPoint: () => void;
  onPreviousPoint: () => void;
  positions?: Partial<{
    top: number;
    right: number;
    left: number;
    bottom: number;
  }>;
  filters: BlockPointsMapControlFilters;
  onControlAction: (
    actionType: BlockPointsMapControlActionTypes,
    payload: BlockPointsMapControlFilters
  ) => void;
} & BaseRequiredDialogProps;

export const BlockPointDetailsControl = memo(
  ({
    open,
    onClose,
    point,
    onNextPoint,
    onPreviousPoint,
    stages,
    positions = { right: 8, top: 8 },
    onControlAction,
    block,
    filters,
  }: BlockPointDetailsControlProps) => {
    const { t } = useTranslation();

    const imageUrl = useMemo<string | undefined>(() => {
      if (point?.leftCameraImgPath) {
        const imgUrl = new URL(
          point.leftCameraImgPath.replace(RegExp('s3://'), config.api.imageUrl)
        );

        imgUrl.searchParams.append('format', 'jpg');

        return imgUrl.toString();
      }

      return undefined;
    }, [point?.leftCameraImgPath]);

    /** Filtering Logic & State */

    const displayStages =
      filters.stages && filters.stages.length > 0
        ? stages.filter((stage) => filters.stages?.includes(stage.id))
        : stages;
    const [showFilterBar, setShowFilterBar] = useState<boolean>(
      !!filters.stages || !!filters.sizes
    );
    const handleStageFilterChange = useCallback(
      (newStagesFilter: BlockPointsMapControlFilters['stages']) => {
        onControlAction('FILTER', { ...filters, stages: newStagesFilter });
      },
      [onControlAction, filters]
    );

    if (!open) return null;

    const pointActions = (
      <Stack direction='row' sx={{ justifyContent: 'space-between' }}>
        <DiagnosticFeature>
          <LoadingSkeleton loading={!point} width={80} height={16}>
            <PointIdCopyControl uniqueId={point?.id ?? ''} />
          </LoadingSkeleton>
        </DiagnosticFeature>
        <Stack direction='row' spacing={1}>
          <ArrowButton size='large' onClick={onPreviousPoint}>
            <ArrowBack fontSize='inherit' />
          </ArrowButton>
          <ArrowButton onClick={onNextPoint}>
            <ArrowForward fontSize='inherit' />
          </ArrowButton>
        </Stack>
      </Stack>
    );

    const berryCountActions = (
      <IconButton
        sx={{
          color: (theme) =>
            showFilterBar
              ? theme.vars.palette.action.active
              : theme.vars.palette.text.secondary,
        }}
        size='small'
        onClick={() => setShowFilterBar((prevState) => !prevState)}
      >
        <FilterIcon fontSize='inherit' />
      </IconButton>
    );

    const footerActions = (
      <Button variant='contained' onClick={onClose}>
        {t('Close')}
      </Button>
    );

    return (
      <MapControl
        aria-label='Point Metadata'
        tabIndex={-1}
        sx={{
          position: 'absolute',
          ...positions,
          width: 350,
        }}
      >
        {/* Top Section: Image with Point selected title and navigation */}
        <MapControlSection title={t('Point selected')} actions={pointActions}>
          {point ? (
            <PointImage src={imageUrl} enableColorCorrection />
          ) : (
            <Typography>Unable to display Point image</Typography>
          )}
        </MapControlSection>

        {/* Header: Berry count with filter */}
        <MapControlHeader
          title={t('Berry count')}
          actions={berryCountActions}
        />

        <MapControlContent>
          {/* Filter Bar */}
          {showFilterBar && (
            <MapControlSection>
              <Stack
                direction='row'
                spacing={2}
                sx={{ width: '100%', paddingTop: 2, paddingBottom: 2 }}
              >
                <Stack direction='row' spacing={2} sx={{ minWidth: 100 }}>
                  <Typography variant='body2' noWrap>
                    View By
                  </Typography>
                  <Select
                    variant='minimal'
                    value={'stage'}
                    onChange={() => {}}
                    options={[
                      { value: 'stage', label: 'Stage' },
                      // { value: 'size', label: 'Size' },
                    ]}
                  />
                </Stack>
                <Stack
                  direction='row'
                  spacing={2}
                  sx={{ minWidth: 100, maxWidth: 150, margin: 'auto' }}
                >
                  <Typography variant='body2'>Stage</Typography>
                  <StageSelect
                    value={displayStages.map((s) => s.id)}
                    onChange={handleStageFilterChange}
                    varietyId={block.varietyID || ''}
                  />
                </Stack>
              </Stack>
            </MapControlSection>
          )}

          {/* Point Data Section */}
          <MapControlSection>
            {point ? (
              <PointStageDataDisplay point={point} stages={displayStages} />
            ) : (
              <Typography>Unable to display Point information</Typography>
            )}
          </MapControlSection>
        </MapControlContent>

        <MapControlFooter actions={footerActions} />
      </MapControl>
    );
  }
);

const ArrowButton = styled((props: IconButtonProps) => {
  return <IconButton {...props} size='small'></IconButton>;
})();
